/**
 * The subscription vocabulary — the two ORTHOGONAL axes this panel talks about,
 * and the order each is read in.
 *
 * Status and tier are a different question about the same clinic, and the whole
 * design turns on not conflating them: a پایه clinic is ACTIVE and entitled but
 * capped, while a lapsed طلایی clinic is EXPIRED and still holds CLINIC_GOLD —
 * it loses entitlement, not the tier it bought.
 *
 * WHY THIS IS APP-LEVEL AND NOT `statusColors` FROM THE FRAMEWORK. The kit's
 * `statusColors` covers exactly two vocabularies — the appointment lifecycle and
 * patient-record states — and DESIGN.md is explicit that a third one stays out,
 * because apps spread that map into one merged lookup and a new key would
 * clobber an existing one. A subscription's «completed» does not exist and its
 * «active» is not an appointment's. So this is the ONE place the panel names
 * these states, and there is no second copy anywhere.
 *
 * Labels are NOT here: they are catalogue keys (`clinics.status.ACTIVE`,
 * `clinics.tier.CLINIC_GOLD`), so they translate. What lives here is the part
 * that is not language — which states exist, their order, and their colour.
 */

// ── Status ───────────────────────────────────────────────────────────────────

/**
 * "No subscription row at all", which is NOT one of the service's statuses and
 * must never be dressed up as one. A clinic with no row is reported by the
 * subscription service as entitled (it fails open), so folding it into EXPIRED —
 * or into ACTIVE — is exactly how un-provisioned clinics stay invisible to ops.
 * It is a filter value and a badge of its own.
 */
export const UNPROVISIONED = 'UNPROVISIONED';

export const STATUS_TRIALING = 'TRIALING';
export const STATUS_ACTIVE = 'ACTIVE';
export const STATUS_PAST_DUE = 'PAST_DUE';
export const STATUS_EXPIRED = 'EXPIRED';
export const STATUS_SUSPENDED = 'SUSPENDED';

/** Every status the service can report. */
export const STATUSES = [
  STATUS_TRIALING, STATUS_ACTIVE, STATUS_PAST_DUE, STATUS_EXPIRED, STATUS_SUSPENDED,
];

/**
 * The status filter's options, in the order an operator scans them: everything,
 * then the two that need work most, then the healthy ones, then the rest.
 *
 * `PAST_DUE` and `SUSPENDED` are in this list because they were MISSING from
 * it: the old filter offered five of seven states and left out precisely the two
 * an ops panel exists to act on.
 */
export const STATUS_FILTERS = [
  'ALL', UNPROVISIONED, STATUS_PAST_DUE, STATUS_TRIALING,
  STATUS_ACTIVE, STATUS_EXPIRED, STATUS_SUSPENDED,
];

/**
 * Sort order for the status column: worst first, so ordering by status puts the
 * work at the top. Not alphabetical, and not the service's enum order — neither
 * of those answers «what needs me next».
 */
export const STATUS_SEVERITY = [
  UNPROVISIONED, STATUS_SUSPENDED, STATUS_EXPIRED,
  STATUS_PAST_DUE, STATUS_TRIALING, STATUS_ACTIVE,
];

/**
 * Framework `Badge` variant per status.
 *
 * `TRIALING` is `outline` rather than a colour on purpose: a trial is not a
 * problem and not an achievement, and the colour budget (DESIGN-CONCEPT §10)
 * says semantic colour is spent on things that demand attention. A list where
 * every row is tinted communicates nothing.
 */
export const STATUS_VARIANT = {
  [STATUS_TRIALING]: 'outline',
  [STATUS_ACTIVE]: 'success',
  [STATUS_PAST_DUE]: 'warning',
  [STATUS_EXPIRED]: 'destructive',
  [STATUS_SUSPENDED]: 'destructive',
  [UNPROVISIONED]: 'outline',
};

// ── Tier ─────────────────────────────────────────────────────────────────────

export const CLINIC_BASIC = 'CLINIC_BASIC';
export const CLINIC_SILVER = 'CLINIC_SILVER';
export const CLINIC_GOLD = 'CLINIC_GOLD';

/** Ascending capability — the order the panel lists tiers in, and ranks them by. */
export const TIER_ORDER = [CLINIC_BASIC, CLINIC_SILVER, CLINIC_GOLD];

export const TIER_VARIANT = {
  [CLINIC_BASIC]: 'outline',
  [CLINIC_SILVER]: 'default',
  [CLINIC_GOLD]: 'success',
};

/**
 * How far up the ladder a capability set sits, or -1 for one that is not on it.
 * A clinic with no tier cannot be downgraded, which is why "unknown" has to be
 * distinguishable rather than clamped to 0.
 */
export const tierRank = (capabilitySet) => TIER_ORDER.indexOf(capabilitySet);

// ── Catalogue keys ───────────────────────────────────────────────────────────
//
// Deliberately functions rather than maps of strings: the label is a KEY, and a
// key resolved at the render site through `t` is what makes the panel
// translatable. A map of Persian strings here is the shape this replaced.

export const statusLabelKey = (status) => `clinics.status.${status || UNPROVISIONED}`;

/**
 * The catalogue key for a capability set, or null when the panel has never been
 * taught this tier.
 *
 * Null is the signal to echo the RAW CODE at the call site. An unknown value
 * comes back as itself — never a dash, and never silently as طلایی: if the
 * service starts reporting `CLINIC_PLATINUM`, an operator who sees that in the
 * column can tell us, whereas a dash looks like missing data and a
 * wrong-but-plausible label is never noticed at all.
 */
export const tierLabelKey = (capabilitySet) =>
  (TIER_ORDER.includes(capabilitySet) ? `clinics.tier.${capabilitySet}` : null);

// ── Origins and period sources ───────────────────────────────────────────────

export const ORIGIN_KEY = {
  GRANDFATHERED: 'clinic.origin.grandfathered',
  SALES: 'clinic.origin.sales',
  SELF_SERVICE: 'clinic.origin.selfService',
};

export const PERIOD_SOURCE_KEY = {
  GATEWAY: 'clinic.periods.source.gateway',
  MANUAL: 'clinic.periods.source.manual',
  INITIAL: 'clinic.periods.source.initial',
};

/**
 * The subscription service's event types → catalogue keys.
 *
 * An event type this map has never seen is rendered as its own raw string, in an
 * LTR island, for the same reason an unknown tier is: a new event type the panel
 * has not been taught should be legible and reportable, not swallowed.
 */
export const EVENT_TYPE_KEY = {
  'clinic.subscription.created': 'clinic.eventType.created',
  'clinic.subscription.activated': 'clinic.eventType.activated',
  'clinic.subscription.renewed': 'clinic.eventType.renewed',
  'clinic.subscription.reactivated': 'clinic.eventType.reactivated',
  'clinic.subscription.past_due': 'clinic.eventType.pastDue',
  'clinic.subscription.expired': 'clinic.eventType.expired',
  'clinic.subscription.suspended': 'clinic.eventType.suspended',
  'clinic.subscription.unsuspended': 'clinic.eventType.unsuspended',
  'clinic.subscription.override_granted': 'clinic.eventType.overrideGranted',
  'clinic.subscription.not_renewing_set': 'clinic.eventType.notRenewingSet',
  'clinic.subscription.trial_ended': 'clinic.eventType.trialEnded',
  'clinic.subscription.plan_changed': 'clinic.eventType.planChanged',
  'clinic.subscription.expiring_soon': 'clinic.eventType.expiringSoon',
};
